import { EasyDoctorProvider } from '@easydoctor/ui';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { createQueryClient } from './api/query-client';
import { RequireRole } from './auth/RequireRole';
import { RequireSession } from './auth/RequireSession';
import { AccessoPage } from './pages/accesso';
import { McpAuthorizationErrorPage, McpConsentPage } from './pages/autorizzazione-mcp';
import { ArchivioPage, CodaPage } from './pages/coda';
import { QueueSettingsPage } from './pages/impostazioni/coda';
import { ConsumersPage } from './pages/impostazioni/consumer';
import { ImpostazioniLayout } from './pages/impostazioni/ImpostazioniLayout';
import { ProductsPage } from './pages/impostazioni/prodotti';
import { TeamPage } from './pages/impostazioni/team';
import { PostaNonAssociataPage } from './pages/posta-non-associata';
import { ProfiloPage } from './pages/profilo';
import { StatistichePage } from './pages/statistiche';
import { TicketDetailPanel } from './pages/ticket';
import { SuiteShell } from './shell/SuiteShell';

const defaultQueryClient = createQueryClient();

// La Guida rende il Markdown con le sue librerie: un pezzo di bundle a parte, caricato alla prima visita (FED-002).
const GuidaPage = lazy(() => import('./pages/guida').then((module) => ({ default: module.GuidaPage })));

function GuidaLoading() {
  return (
    <Stack direction="row" spacing={2} alignItems="center" role="status">
      <CircularProgress size={20} />
      <Typography>Caricamento della Guida…</Typography>
    </Stack>
  );
}

/**
 * Rotte della web app: `/accesso` e l'errore dell'autorizzazione MCP sono le sole pagine senza
 * sessione; tutto il resto sta dietro `RequireSession` e, tranne il consenso dell'MCP
 * (`/autorizzazione-mcp`, #151), dentro la shell della suite. La Coda è la home e `/ticket/:code` apre il
 * dettaglio in un pannello sopra la coda; l'Archivio (#123) è `/archivio`, con il dettaglio sopra
 * l'archivio su `/archivio/ticket/:code`. Statistiche e Impostazioni sono dell'amministratore. La Guida
 * (`/guida/*`, PAGE-010) e il Profilo (`/profilo`, #152) sono di tutti i ruoli.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="accesso" element={<AccessoPage />} />
      {/* Autorizzazione dei client MCP (#151): fuori dalla shell; l'errore anche senza sessione. */}
      <Route path="autorizzazione-mcp/errore" element={<McpAuthorizationErrorPage />} />
      <Route element={<RequireSession />}>
        <Route path="autorizzazione-mcp" element={<McpConsentPage />} />
        <Route element={<SuiteShell />}>
          {/* La coda è la home; il dettaglio è una rotta figlia resa in un pannello sopra (PAGE-001, PAGE-002). */}
          <Route element={<CodaPage />}>
            <Route index element={null} />
            <Route path="ticket/:code" element={<TicketDetailPanel />} />
          </Route>
          {/* Archivio (#123): Risolto, Chiuso e Scartato, con la tabella e il dettaglio della coda. */}
          <Route path="archivio" element={<ArchivioPage />}>
            <Route index element={null} />
            <Route path="ticket/:code" element={<TicketDetailPanel />} />
          </Route>
          {/* Posta non associata (#42): tutto il team, nessun ruolo richiesto. */}
          <Route path="posta-non-associata" element={<PostaNonAssociataPage />} />
          {/* Guida (#57): tutti i ruoli, anche le pagine per l'amministratore (PRD-006 OQ-1). */}
          <Route
            path="guida/*"
            element={
              <Suspense fallback={<GuidaLoading />}>
                <GuidaPage />
              </Suspense>
            }
          />
          {/* Profilo (#152): tutti i ruoli, con le proprie connessioni di Claude e Cursor. */}
          <Route path="profilo" element={<ProfiloPage />} />
          <Route
            path="statistiche"
            element={
              <RequireRole role="amministratore">
                <StatistichePage />
              </RequireRole>
            }
          />
          <Route
            path="impostazioni"
            element={
              <RequireRole role="amministratore">
                <ImpostazioniLayout />
              </RequireRole>
            }
          >
            <Route index element={<Navigate to="team" replace />} />
            <Route path="team" element={<TeamPage />} />
            <Route path="coda" element={<QueueSettingsPage />} />
            <Route path="consumer" element={<ConsumersPage />} />
            <Route path="prodotti" element={<ProductsPage />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}

/** Radice della web app; `queryClient` è sostituibile nei test (quello di default è condiviso). */
export function App({ queryClient = defaultQueryClient }: { queryClient?: QueryClient } = {}) {
  return (
    <EasyDoctorProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </QueryClientProvider>
    </EasyDoctorProvider>
  );
}
